/**
 * Database utilities for Supabase clients in Admin UI.
 *
 * This module provides database connection utilities that work in:
 * - Browser environments (Vite/React)
 * - Node.js environments
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import {
  detectRuntimeEnvironment,
  getEnvVar,
  RuntimeEnvironment,
  logger,
} from '@musedrops/shared';

export type Environment = RuntimeEnvironment;

/**
 * Configuration for creating a Supabase client
 */
export interface SupabaseConfig {
  url?: string;
  anonKey?: string;
  serviceRoleKey?: string;
  environment?: Environment;
}

/**
 * Creates a Supabase client configured for the current environment.
 *
 * Automatically detects the environment and uses appropriate environment variables:
 * - Browser: Uses import.meta.env (Vite)
 * - Node.js: Uses process.env
 * - Deno: Uses Deno.env
 *
 * @param options Configuration options
 * @returns Configured Supabase client
 * @throws {Error} If required environment variables are not set
 */
export function createSupabaseClient(
  options: Partial<SupabaseConfig> = {}
): SupabaseClient {
  const { url, anonKey, serviceRoleKey, environment } = options;

  // Detect environment if not specified
  const env = environment || detectRuntimeEnvironment();

  // Get configuration based on environment
  const config = getEnvironmentConfig(url, anonKey, serviceRoleKey, env);

  // Validate required configuration
  if (!config.url) {
    throw new Error(
      'Supabase URL is required. Set SUPABASE_URL environment variable.'
    );
  }

  if (!config.key) {
    throw new Error(
      'Supabase key is required. Set SUPABASE_ANON_KEY or SUPABASE_SERVICE_ROLE_KEY environment variable.'
    );
  }

  return createClient(config.url, config.key, {
    auth: {
      persistSession: env === 'browser',
      autoRefreshToken: env === 'browser',
      detectSessionInUrl: env === 'browser',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      storage:
        env === 'browser'
          ? (globalThis as any).window?.localStorage
          : undefined,
    },
  });
}

/**
 * Creates a Supabase client with service role access for admin operations.
 * Uses service role key when available, falls back to anon key.
 */
export function createServiceClient(
  options: Partial<SupabaseConfig> = {}
): SupabaseClient {
  const env = options.environment || detectRuntimeEnvironment();

  // Try to get service role key first, fall back to anon key
  let serviceKey = options.serviceRoleKey;
  if (!serviceKey) {
    serviceKey = getEnvVar('SUPABASE_SERVICE_ROLE_KEY', { runtime: env });
    if (!serviceKey) {
      logger.warn(
        'Service role key not found, falling back to anon key. Some operations may be restricted.'
      );
      serviceKey = getEnvVar('SUPABASE_ANON_KEY', { runtime: env });
    }
  }

  const config: SupabaseConfig = {
    ...options,
    environment: env,
  };

  if (serviceKey) {
    config.serviceRoleKey = serviceKey;
  }

  return createSupabaseClient(config);
}

/**
 * Gets environment configuration based on the runtime
 */
function getEnvironmentConfig(
  url?: string,
  anonKey?: string,
  serviceRoleKey?: string,
  environment?: Environment
): { url: string; key: string } {
  const env = environment || detectRuntimeEnvironment();

  const config = {
    url: url || getEnvVar('SUPABASE_URL', { runtime: env }),
    anonKey: anonKey || getEnvVar('SUPABASE_ANON_KEY', { runtime: env }),
    serviceRoleKey:
      serviceRoleKey ||
      getEnvVar('SUPABASE_SERVICE_ROLE_KEY', { runtime: env }),
  };

  // Determine which key to use (service role takes precedence)
  const key = config.serviceRoleKey || config.anonKey;

  if (!config.url || !key) {
    throw new Error(
      'Missing required Supabase configuration. Check your environment variables.'
    );
  }

  return {
    url: config.url,
    key,
  };
}
